import React, { FC, memo, useCallback, useEffect, useState } from 'react';

import { LeftColumnContent } from '../../../types';

import { LAYERS_ANIMATION_NAME } from '../../../util/windowEnvironment';

import Transition from '../../ui/Transition';
import NewChatStep1 from './NewChatStep1';
import NewChatStep2 from './NewChatStep2';

import NewChannelSelect from './NewChannelSelect';
import NewChannelTypeInfo from './NewChannelTypeInfo';
import AgeRestrictionPolicy from './AgeRestrictionPolicy';
import NewCourseSelect from './NewCourseSelect';
import NewCourseTypeInfo from './NewCourseTypeInfo';

import { withGlobal } from '../../../global';
import { selectTabState } from '../../../global/selectors';
import NewCourseStep1 from './NewCourseStep1';
import useHistoryBack from '../../../hooks/useHistoryBack';

import './NewChat.scss';

export type ChannelType = 'public' | 'private' | 'subscription';
export type CourseType = 'online';

export interface ECreateCourse {
  startDate: string;
  endDate: string;
}

export type OwnProps = {
  isActive: boolean;
  isChannel?: boolean;
  isCourse?: boolean;
  isMediaSale?: boolean;
  onReset: () => void;
};

type StateProps = {
  leftScreen: LeftColumnContent;
};

const RENDER_COUNT = Object.keys(LeftColumnContent).length / 2;

const NewChat: FC<OwnProps & StateProps> = ({
  isActive,
  isChannel = false,
  isCourse,
  leftScreen,
  onReset,
}) => {
  const [newChatMemberIds, setNewChatMemberIds] = useState<string[]>([]);
  const [channelType, setChannelType] = useState<ChannelType>('public');
  const [courseType, setCourseType] = useState<CourseType>('online');
  const [courseData, setCourseData] = useState<ECreateCourse>();

  const [cost, setCost] = useState<string>('');

  const { toggleScreens } = useHistoryBack();

  const handleNextStep = useCallback(() => {
    toggleScreens({
      leftScreen: isChannel
        ? LeftColumnContent.NewChannelStep2
        : LeftColumnContent.NewGroupStep2,
    });
  }, [isChannel]);

  return (
    <Transition
      id='NewChat'
      name={LAYERS_ANIMATION_NAME}
      renderCount={RENDER_COUNT}
      activeKey={leftScreen}
    >
      {(isStepActive) => {
        switch (leftScreen) {
          case LeftColumnContent.NewChannelSelect:
            return (
              <NewChannelSelect
                isActive={isActive}
                onNextStep={() =>
                  toggleScreens({
                    leftScreen: LeftColumnContent.NewChannelStep1,
                  })
                }
                onReset={onReset}
                channelType={channelType}
                setChannelType={setChannelType}
              />
            );
          case LeftColumnContent.NewCoursSelect:
            return (
              <NewCourseSelect
                isActive={isActive}
                onNextStep={() =>
                  toggleScreens({
                    leftScreen: LeftColumnContent.NewCourseStep1,
                  })
                }
                onReset={onReset}
                courseType={courseType}
                setCourseType={setCourseType}
              />
            );
          case LeftColumnContent.NewCourseTypeInfo:
            return (
              <NewCourseTypeInfo
                isActive={isActive}
                onNextStep={() =>
                  toggleScreens({
                    leftScreen: LeftColumnContent.NewCourseStep1,
                  })
                }
                onReset={onReset}
              />
            );
          case LeftColumnContent.NewChannelTypeInfo:
            return (
              <NewChannelTypeInfo
                isActive={isActive}
                onNextStep={() =>
                  toggleScreens({
                    leftScreen: LeftColumnContent.NewChannelStep1,
                  })
                }
                onReset={onReset}
                channelType={channelType}
              />
            );
          case LeftColumnContent.NewChannelStep1:
          case LeftColumnContent.NewGroupStep1:
            return (
              <NewChatStep1
                isChannel={isChannel}
                channelType={channelType}
                cost={cost}
                selectedMemberIds={newChatMemberIds}
                onSelectedMemberIdsChange={setNewChatMemberIds}
                onNextStep={handleNextStep}
                onReset={onReset}
                setCost={setCost}
              />
            );

          case LeftColumnContent.NewGroupStep2:
            return (
              <NewChatStep2
                isChannel={isChannel}
                memberIds={newChatMemberIds}
                onReset={onReset}
                onSelectedIdsChange={setNewChatMemberIds}
              />
            );
          case LeftColumnContent.NewChannelStep2:
            return (
              <NewChatStep2
                isChannel={isChannel}
                memberIds={newChatMemberIds}
                onReset={onReset}
                cost={cost}
                channelType={channelType}
                onSelectedIdsChange={setNewChatMemberIds}
              />
            );
          case LeftColumnContent.NewCourseStep1:
            return (
              <NewCourseStep1
                isActive={isStepActive && isActive}
                onReset={onReset}
                cost={cost}
                setCost={setCost}
                courseData={courseData}
                setCreateCourseData={setCourseData}
                onNextStep={() =>
                  toggleScreens({
                    leftScreen: LeftColumnContent.NewCourseStep2,
                  })
                }
              />
            );

          case LeftColumnContent.NewCourseStep2:
            return (
              <NewChatStep2
                isChannel={true}
                isCourse={true}
                memberIds={newChatMemberIds}
                courseData={courseData}
                onReset={onReset}
                cost={cost}
                channelType={channelType}
                onSelectedIdsChange={setNewChatMemberIds}
              />
            );

          // case LeftColumnContent.NewMediaSaleInfo:
          //   return (
          //     <NewMediaSaleTypeInfo
          //       isActive={isActive}
          //       onNextStep={() =>
          //         onContentChange(LeftColumnContent.NewChannelStep1)
          //       }
          //       onReset={onReset}
          //     />
          //   );
          case LeftColumnContent.AgeRestriction:
            return (
              <AgeRestrictionPolicy isActive={isActive} onReset={onReset} />
            );
          case LeftColumnContent.AgeRestrictionForCourse:
            return (
              <AgeRestrictionPolicy
                isActive={isActive}
                onReset={onReset}
                isCourse={isCourse}
              />
            );
          default:
            return undefined;
        }
      }}
    </Transition>
  );
};

export default memo(
  withGlobal((global): StateProps => {
    const tabState = selectTabState(global);
    const { leftScreen } = tabState;
    return {
      leftScreen,
    };
  })(NewChat)
);
